// INTEGRATIONS (owner 2026-10-29: "a tab that shows all live connections between all tabs
// and systems and acc and procore, what can be downloaded and what can be uploaded and what
// are the triggers and a gate to enable them or disable them, tab should be called
// integrations").
//
// The project's Procore / ACC accounts with their connection state, then every flow -
// Models, Shop drawings, Document Control, the linked 3D model, tunnels - each with what it
// moves (folders or picked files), what starts it, and its switch
// (back/utils/integrationMap). Folders are still chosen where they always were (the
// project's Procore / ACC settings, the Tunnels tab); this tab shows them and turns flows on
// and off.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Switch, Table, Tag, Tooltip } from "antd";
import { CloudDownloadOutlined, CloudUploadOutlined, FileOutlined, FolderOpenOutlined, NodeIndexOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import addNotification from "helpers/notify";
import { AutodeskLogo, ProcoreLogo } from "components/Integrations/ProviderLogos";
import { listIntegrations, switchIntegration } from "api/projectModels";

const AREAS = ["Models", "3D model", "Shop drawings", "Document Control"];
const DIRECTION = {
  download: { icon: <CloudDownloadOutlined />, label: "Download" },
  upload: { icon: <CloudUploadOutlined />, label: "Upload" },
  tunnel: { icon: <NodeIndexOutlined />, label: "Tunnel" },
};

const Logo = ({ provider, size = 16 }) => (provider === "procore" ? <ProcoreLogo connected size={size} /> : <AutodeskLogo connected size={size} />);

function Paths({ what, empty }) {
  if (!what || (!(what.files || []).length && !(what.folders || []).length)) {
    return <small className="text-warning"><WarningOutlined /> {empty || "No folder set"}</small>;
  }
  return (
    <div className="integ-paths">
      {(what.files || []).map((f) => <small key={`f:${f}`} className="d-block models-path" title={f}><FileOutlined /> {f}</small>)}
      {(what.folders || []).map((f) => (
        <small key={`d:${f.path}`} className="d-block models-path" title={f.path}>
          <FolderOpenOutlined /> {f.path}
          {f.exts && f.exts.length < 4 ? <span className="text-muted"> ({f.exts.map((e) => `.${e}`).join(" ")})</span> : null}
        </small>
      ))}
    </div>
  );
}

export default function IntegrationsTab({ projectId, active, onOpenTunnels }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setData(await listIntegrations(projectId));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [projectId]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const flip = async (f, on) => {
    setBusy(f.key);
    try {
      const d = await switchIntegration(projectId, f.control, on);
      setData((x) => ({ ...x, ...d }));
      addNotification(`${f.title}: ${on ? "on" : "off"}`, "success");
    } catch (e) {
      addNotification(e.message, "danger");
    }
    setBusy("");
  };

  if (error) return <Alert type="error" message={error} />;
  if (!data) return <div className="text-muted p-3">Loading...</div>;

  const canEdit = data.canEdit || {};
  const mayFlip = (f) => (f.direction === "tunnel"
    ? canEdit.acc || canEdit.procore
    : canEdit[f.provider]);

  const columns = [
    {
      title: "",
      key: "dir",
      width: 104,
      render: (_, f) => <span className="transfer-dir">{DIRECTION[f.direction].icon} {DIRECTION[f.direction].label}</span>,
    },
    {
      title: "Connection",
      key: "title",
      width: 300,
      render: (_, f) => (
        <span className="models-source">
          <Logo provider={f.provider} />
          <span>
            <b>{f.title}</b>
            <small className="d-block text-muted">
              {f.direction === "tunnel" ? f.destinationName : [f.account, f.actor ? `acts as ${f.actor}` : ""].filter(Boolean).join(" · ")}
              {f.direction !== "tunnel" && f.connected === false ? <Tag color="red" className="ms-1">Not connected</Tag> : null}
            </small>
          </span>
        </span>
      ),
    },
    {
      title: "What moves",
      key: "what",
      render: (_, f) => (f.direction === "tunnel" ? (
        <div>
          <div><small className="text-muted">From</small><Paths what={f.from} /></div>
          <div><small className="text-muted">To</small><Paths what={f.to} /></div>
          {(f.exts || []).length ? <small className="text-muted">{f.exts.map((e) => `.${e}`).join(" ")} only</small> : null}
          {f.onlyNew ? <Tag className="ms-1">Only new versions</Tag> : null}
        </div>
      ) : <Paths what={f.what} empty={f.area === "Models" && f.direction === "download" ? "No download folder or file set" : undefined} />),
    },
    {
      title: "Trigger",
      key: "trigger",
      width: 260,
      render: (_, f) => (
        <div>
          <small>{f.trigger}</small>
          {f.lastRun && f.lastRun.at ? (
            <small className={`d-block ${f.lastRun.error ? "text-danger" : "text-muted"}`}>
              Last check {moment(f.lastRun.at).fromNow()}{f.lastRun.note ? `: ${f.lastRun.note}` : ""}{f.lastRun.error ? `: ${f.lastRun.error}` : ""}
            </small>
          ) : null}
        </div>
      ),
    },
    {
      title: "On",
      key: "on",
      width: 70,
      render: (_, f) => (
        <Tooltip title={!mayFlip(f) ? "Only people who manage the company's Procore / ACC connections can switch this"
          : !f.configured ? "Nothing is set to move yet"
            : f.on ? `On: ${f.trigger || "this flow runs on its trigger"}. Turn it off to stop it without losing its folders.`
              : "Off: nothing moves on this flow. Turn it on to let its trigger run again."}>
          <Switch
            checked={!!f.on}
            loading={busy === f.key}
            disabled={!mayFlip(f)}
            onChange={(v) => flip(f, v)}
            data-testid={`integ-switch-${f.key}`}
          />
        </Tooltip>
      ),
    },
  ];

  const byArea = AREAS.map((a) => ({ area: a, flows: data.flows.filter((f) => f.area === a) })).filter((g) => g.flows.length);

  return (
    <div className="models-integrations" data-testid="models-integrations">
      {data.transfersPaused ? (
        <Alert type="warning" showIcon className="mb-2" message="All transfers on this project are paused on the Transfers tab - nothing below moves until they are resumed." />
      ) : null}
      <div className="models-toolbar">
        <b>Accounts</b>
        <span className="models-spacer" />
        <Tooltip title="Open the Tunnels tab: the tunnels of this project, each with its manual trigger and its keep-synced toggle">
          <Button icon={<NodeIndexOutlined />} onClick={onOpenTunnels}>Tunnels</Button>
        </Tooltip>
        <Tooltip title="Read every connection and flow again now">
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Tooltip>
      </div>
      <div className="integ-accounts mb-3">
        {data.accounts.length ? data.accounts.map((a) => (
          <div key={a.id} className="integ-account">
            <Logo provider={a.provider} size={20} />
            <div>
              <b>{a.platform}</b> · {a.label}{a.primary ? <Tag className="ms-1">Primary</Tag> : null}
              <small className="d-block text-muted">
                {a.email}{a.method === "browser" ? ` · browser connector${a.seenAt ? `, seen ${moment(a.seenAt).fromNow()}` : ""}` : a.method ? ` · ${a.method}` : ""}
              </small>
            </div>
            <Tag color={a.status === "connected" ? "green" : "red"}>{a.status === "connected" ? "Connected" : a.status || "Not connected"}</Tag>
          </div>
        )) : <Empty description="No Procore or ACC account is connected for this company" />}
      </div>
      {byArea.length ? byArea.map((g) => (
        <div key={g.area} className="mb-3">
          <h6 className="integ-area">{g.area}</h6>
          <Table rowKey="key" size="small" pagination={false} columns={columns} dataSource={g.flows} />
        </div>
      )) : <Empty description="No Procore or ACC destination is set on this project yet" />}
    </div>
  );
}
